<template>
  <div class="note-records-list p-4">
    <RecordListPrintHeader title="ノート記録日数/緊急連絡先/前所属・経歴" />

    <div
      v-for="member in members"
      :key="member.id"
      :data-member-id="member.id"
      class="note-records-list__user-row border mb-4">
      <div class="d-flex">
        <!-- ユーザー情報（左パネル） -->
        <div
          class="note-records-list__user-info note-records-list__user-info--clickable"
          @click="goToProfile(member.id)">
          <RecordListMemberInfo
            :member="member"
            :highlighted="member.id === highlightMemberId" />
        </div>

        <!-- データエリア（右パネル：3カラム） -->
        <div class="note-records-list__data-area d-flex">
          <!-- 数値列 -->
          <div class="note-records-list__col note-records-list__col--numbers">
            <div class="note-records-list__section-title">
              ノート記録日数
            </div>
            <div class="note-records-list__record-days">
              <span class="fs-lg font-weight-bold">{{ member.recordDays }}</span>
              <span class="fs-s ml-1">日</span>
            </div>
            <div class="note-records-list__section-title">
              自己評価月数
            </div>
            <div class="note-records-list__record-days">
              <span class="fs-lg font-weight-bold">{{ member.selfEvalMonths }}</span>
              <span class="fs-s ml-1">ヶ月</span>
            </div>
          </div>

          <!-- 緊急連絡先列 -->
          <div class="note-records-list__col note-records-list__col--emergency">
            <div class="note-records-list__section-title">
              緊急連絡先
            </div>
            <div
              v-if="!getEmergencyContactsForMember(member.id).length"
              class="fs-s text--grey05">
              登録なし
            </div>
            <table
              v-else
              class="note-records-list__table">
              <thead>
                <tr>
                  <th>続柄</th>
                  <th>電話番号</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="contact in getEmergencyContactsForMember(member.id)"
                  :key="contact.id">
                  <td>{{ contact.relationship | relationshipToText }}</td>
                  <td>{{ contact.phoneNumber | phoneNumber }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 前所属・経歴列 -->
          <div class="note-records-list__col note-records-list__col--history">
            <div class="note-records-list__section-title">
              前所属・経歴
            </div>
            <div
              v-if="!getPastDepartmentsForMember(member.id).length"
              class="fs-s text--grey05">
              登録なし
            </div>
            <table
              v-else
              class="note-records-list__table">
              <thead>
                <tr>
                  <th>年代</th>
                  <th>所属チーム名</th>
                  <th>ポジション</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="dept in getPastDepartmentsForMember(member.id)"
                  :key="dept.id">
                  <td>{{ educationLevelText(dept.educationLevel) }}</td>
                  <td>{{ dept.name }}</td>
                  <td>{{ dept.position || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import RecordListPrintHeader from '@/components/view/records/RecordListPrintHeader.vue'
import RecordListMemberInfo from '@/components/view/records/RecordListMemberInfo.vue'
import { buildMemberFilterApiParams, buildOrderingByFilterQuery, resolveFilterQuery } from '@/helper/records'

const EDUCATION_LEVEL_LABELS = {
  elementary: '小学生',
  junior_high: '中学生',
  high_school: '高校生',
  university: '大学生',
}

export default {
  components: {
    RecordListPrintHeader,
    RecordListMemberInfo,
  },
  data() {
    return {
      members: [],
      emergencyContactsByUserId: {},
      pastDepartmentsByUserId: {},
      highlightMemberId: null,
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('recordProfile', ['filterParams']),
  },
  async created() {
    this.$store.commit('startLoading')
    try {
      await this.fetchData()
    } finally {
      this.$store.commit('finishLoading')
    }
    this.$nextTick(() => {
      const memberId = this.$store.state.recordProfile.returnMemberId
      if (memberId) {
        this.$store.commit('recordProfile/clearReturnMemberId')
        this.highlightMemberId = memberId
        const el = document.querySelector(`[data-member-id="${memberId}"]`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    })
  },
  methods: {
    async fetchData() {
      const q = resolveFilterQuery(this.$route.query, this.filterParams)
      const params = buildMemberFilterApiParams(q)
      const [activityRes, emergencyRes, pastRes, usersRes] = await Promise.all([
        this.$axios.get('/admin/profiles/activity-record-days/', { params }),
        this.$axios.get('/admin/profiles/emergency-contacts/', { params }),
        this.$axios.get('/admin/profiles/past-departments/', { params }),
        this.$axios.get('/users/', { params: { ...params, ordering: buildOrderingByFilterQuery(q), limit: 999999 } }),
      ])
      const users = usersRes.data.results || []
      const activityMap = activityRes.data.reduce((acc, item) => {
        acc[item.id] = { recordDays: item.recordDays, selfEvalMonths: item.selfEvalMonths }
        return acc
      }, {})
      this.members = users.map(user => ({
        ...user,
        ...activityMap[user.id] || { recordDays: 0, selfEvalMonths: 0 },
      }))
      this.emergencyContactsByUserId = emergencyRes.data.reduce((acc, item) => {
        acc[item.id] = item.emergencyContacts
        return acc
      }, {})
      this.pastDepartmentsByUserId = pastRes.data.reduce((acc, item) => {
        acc[item.id] = item.pastDepartments
        return acc
      }, {})
    },
    getEmergencyContactsForMember(userId) {
      return this.emergencyContactsByUserId[userId] || []
    },
    getPastDepartmentsForMember(userId) {
      return this.pastDepartmentsByUserId[userId] || []
    },
    educationLevelText(value) {
      return EDUCATION_LEVEL_LABELS[value] || value
    },
    goToProfile(memberId) {
      this.$router.push({
        path: `/members/${memberId}/profile`,
        query: { backTo: 'recordList' },
      })
    },
  },
}
</script>

<style lang="scss">
@page {
  size: A4 landscape;
}
</style>

<style lang="scss" scoped>
.note-records-list {
  background-color: white;
  min-height: 100vh;

  &__user-row {
    page-break-inside: avoid;
  }

  &__user-info {
    width: 180px;
    flex-shrink: 0;
    padding: 12px;
    border-right: 1px solid $grey11;

    &--clickable {
      cursor: pointer;

      &:hover {
        background-color: $grey03;
      }
    }
  }

  &__data-area {
    flex: 1;
    padding: 12px;
    gap: 16px;
  }

  &__col {
    &--numbers {
      width: 120px;
      flex-shrink: 0;
    }

    &--emergency {
      flex: 1;
    }

    &--history {
      flex: 2;
    }
  }

  &__section-title {
    font-size: $fs-s;
    font-weight: bold;
    border-bottom: 2px solid $l-blue;
    padding-bottom: 4px;
    margin-bottom: 8px;
  }

  &__record-days {
    margin-bottom: 8px;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: $fs-s;

    th,
    td {
      border: 1px solid $grey11;
      padding: 4px 8px;
      text-align: left;
    }

    th {
      background-color: $grey03;
      font-weight: bold;
    }
  }
}

@media (max-width: 767px) {
  .note-records-list {
    &__user-row > .d-flex {
      flex-direction: column;
    }

    &__user-info {
      width: 100%;
      border-right: none;
      border-bottom: 1px solid $grey11;
    }

    &__data-area {
      flex-direction: column;
      gap: 8px;
    }

    &__col {
      &--numbers,
      &--emergency,
      &--history {
        width: 100%;
      }

      &--numbers {
        display: grid;
        grid-template-rows: auto auto;
        grid-auto-flow: column;
        column-gap: 16px;
      }
    }

    &__section-title {
      margin-bottom: 4px;
    }

    &__record-days {
      margin-bottom: 4px;
    }
  }
}
</style>
